import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, IconButton, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar,
  CircularProgress, Chip, Grid, List, ListItem, ListItemText, ListItemIcon,
  ListItemSecondaryAction, Divider, useTheme, alpha, Avatar, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  QrCode as QrCodeIcon,
  Refresh as RefreshIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';

interface WhatsAppInstance {
  id: string;
  name: string;
  instance_name?: string;
  instance_id?: string; // Para Z-API
  phone_number?: string;
  is_connected: boolean;
  last_activity?: string;
  qr_code?: string;
  current_status?: string;
  current_phone?: string;
  provider?: 'evolution' | 'z-api'; // Tipo de provider
  status?: string; // Para Z-API
}

interface QRCodeData {
  instance_name: string;
  qr_code: string;
  status: 'disconnected' | 'connecting' | 'connected' | 'error';
  phone_number?: string;
  last_update: string;
}

const WhatsAppQR: React.FC = () => {
  const theme = useTheme();
  const API_BASE_URL = getApiUrl();

  const [instances, setInstances] = useState<WhatsAppInstance[]>([]);
  const [evolutionInstances, setEvolutionInstances] = useState<WhatsAppInstance[]>([]);
  const [zApiInstances, setZApiInstances] = useState<WhatsAppInstance[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<'all' | 'evolution' | 'z-api'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  
  // QR Code dialog
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<WhatsAppInstance | null>(null);
  const [qrData, setQrData] = useState<QRCodeData | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [monitoring, setMonitoring] = useState<Set<string>>(new Set());

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(null);
  };

  const fetchInstances = useCallback(async () => {
    try {
      setLoading(true);
      
      // Buscar instâncias Evolution API
      let evolutionData: WhatsAppInstance[] = [];
      try {
        const evolutionResponse = await axios.get(`${API_BASE_URL}/whatsapp-qr/instances/status`);
        evolutionData = (evolutionResponse.data.data || []).map((inst: any) => ({
          ...inst,
          provider: 'evolution' as const
        }));
        setEvolutionInstances(evolutionData);
      } catch (err) {
        console.warn('Erro ao buscar instâncias Evolution:', err);
        setEvolutionInstances([]);
      }

      // Buscar instâncias Z-API
      let zApiData: WhatsAppInstance[] = [];
      try {
        const zApiResponse = await axios.get(`${API_BASE_URL}/z-api-instances`);
        zApiData = (zApiResponse.data.data || []).map((inst: any) => ({
          id: inst.id,
          name: inst.name,
          instance_id: inst.instance_id,
          phone_number: inst.phone_number,
          is_connected: inst.is_connected || inst.status === 'active' || inst.status === 'connected',
          status: inst.status,
          provider: 'z-api' as const,
          current_status: inst.status === 'active' ? 'connected' : inst.status === 'connected' ? 'connected' : inst.status === 'connecting' ? 'connecting' : 'disconnected'
        }));
        setZApiInstances(zApiData);
      } catch (err) {
        console.warn('Erro ao buscar instâncias Z-API:', err);
        setZApiInstances([]);
      }

      // Atualizar lista combinada
      let combined: WhatsAppInstance[] = [];
      if (selectedProvider === 'all' || selectedProvider === 'evolution') {
        combined = [...combined, ...evolutionData];
      }
      if (selectedProvider === 'all' || selectedProvider === 'z-api') {
        combined = [...combined, ...zApiData];
      }
      setInstances(combined);
    } catch (err: any) {
      console.error('Erro ao buscar instâncias:', err);
      setError(err.response?.data?.error || 'Erro ao carregar instâncias');
      showSnackbar('Erro ao carregar instâncias', 'error');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, selectedProvider]);

  // Atualizar lista quando o provider selecionado mudar
  useEffect(() => {
    let combined: WhatsAppInstance[] = [];
    if (selectedProvider === 'all' || selectedProvider === 'evolution') {
      combined = [...combined, ...evolutionInstances];
    }
    if (selectedProvider === 'all' || selectedProvider === 'z-api') {
      combined = [...combined, ...zApiInstances];
    }
    setInstances(combined);
  }, [selectedProvider, evolutionInstances, zApiInstances]);

  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'success';
      case 'connecting': return 'warning';
      case 'disconnected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircleIcon />;
      case 'connecting': return <WarningIcon />;
      case 'disconnected': return <ErrorIcon />;
      default: return <ErrorIcon />;
    }
  };

  // Função auxiliar para obter o identificador da instância
  const getInstanceIdentifier = (instance: WhatsAppInstance): string => {
    return instance.instance_name || instance.instance_id || instance.id;
  };

  const handleGetQRCode = async (instance: WhatsAppInstance) => {
    try {
      setQrLoading(true);
      setSelectedInstance(instance);
      setQrDialogOpen(true);

      if (instance.provider === 'z-api' && instance.instance_id) {
        // Buscar QR Code da Z-API
        const response = await axios.get(`${API_BASE_URL}/z-api-instances/${instance.id}/qr-code`);
        
        if (response.data.success) {
          let qrCodeBase64 = response.data.data.qr_code || '';
          
          console.log('QR Code recebido (primeiros 200 chars):', qrCodeBase64.substring(0, 200));
          
          // Se o QR code vier como JSON string, tentar parsear
          if (qrCodeBase64 && qrCodeBase64.startsWith('{')) {
            try {
              const parsed = JSON.parse(qrCodeBase64);
              if (parsed.value) {
                qrCodeBase64 = parsed.value;
              } else if (parsed.base64) {
                qrCodeBase64 = parsed.base64;
              } else if (parsed.qrCode) {
                qrCodeBase64 = parsed.qrCode;
              }
            } catch (e) {
              console.warn('Erro ao parsear QR code JSON:', e);
            }
          }
          
          // Limpar o base64 (remover espaços, quebras de linha, etc)
          if (qrCodeBase64) {
            qrCodeBase64 = qrCodeBase64.trim().replace(/\s/g, '');
            
            // Se o QR code não começar com data:, adicionar o prefixo base64
            if (!qrCodeBase64.startsWith('data:')) {
              // Verificar se já tem o prefixo base64 ou se precisa adicionar
              if (!qrCodeBase64.startsWith('data:image')) {
                qrCodeBase64 = `data:image/png;base64,${qrCodeBase64}`;
              }
            }
            
            console.log('QR Code formatado (primeiros 100 chars):', qrCodeBase64.substring(0, 100));
          }
          
          setQrData({
            instance_name: instance.instance_id || '',
            qr_code: qrCodeBase64,
            status: response.data.data.status || 'disconnected',
            phone_number: response.data.data.phone_number,
            last_update: new Date().toISOString()
          });
          showSnackbar('QR Code obtido com sucesso!', 'success');
        } else {
          showSnackbar('Erro ao obter QR Code', 'error');
        }
      } else if (instance.provider === 'evolution' && instance.instance_name) {
        // Buscar QR Code da Evolution API
        const response = await axios.get(`${API_BASE_URL}/whatsapp-qr/${instance.instance_name}`);
        
        if (response.data.success) {
          setQrData(response.data.data);
          showSnackbar('QR Code obtido com sucesso!', 'success');
        } else {
          showSnackbar('Erro ao obter QR Code', 'error');
        }
      } else {
        showSnackbar('Instância inválida', 'error');
      }
    } catch (err: any) {
      console.error('Erro ao obter QR Code:', err);
      showSnackbar(err.response?.data?.error || 'Erro ao obter QR Code', 'error');
    } finally {
      setQrLoading(false);
    }
  };

  const handleCheckStatus = async (instance: WhatsAppInstance) => {
    if (instance.provider !== 'z-api' || !instance.id) {
      showSnackbar('Apenas instâncias Z-API podem ter o status verificado', 'info');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/z-api-instances/${instance.id}/status`);
      
      if (response.data.success) {
        const statusData = response.data.data;
        const isConnected = statusData.is_connected || statusData.connected || false;
        const instanceStatus = statusData.instance_status || (isConnected ? 'active' : 'disconnected');
        
        // Atualizar a instância na lista
        setZApiInstances(prev => prev.map(inst => {
          if (inst.id === instance.id) {
            return {
              ...inst,
              is_connected: isConnected,
              status: instanceStatus,
              current_status: instanceStatus === 'active' ? 'connected' : instanceStatus === 'connected' ? 'connected' : instanceStatus === 'connecting' ? 'connecting' : 'disconnected',
              phone_number: statusData.phone_number || inst.phone_number,
            };
          }
          return inst;
        }));

        // Atualizar lista combinada
        setInstances(prev => prev.map(inst => {
          if (inst.id === instance.id) {
            return {
              ...inst,
              is_connected: isConnected,
              status: instanceStatus,
              current_status: instanceStatus === 'active' ? 'connected' : instanceStatus === 'connected' ? 'connected' : instanceStatus === 'connecting' ? 'connecting' : 'disconnected',
              phone_number: statusData.phone_number || inst.phone_number,
            };
          }
          return inst;
        }));

        showSnackbar(
          isConnected 
            ? `Instância ${instance.name} está conectada!` 
            : `Instância ${instance.name} está desconectada`,
          isConnected ? 'success' : 'info'
        );
      } else {
        showSnackbar('Erro ao verificar status', 'error');
      }
    } catch (err: any) {
      console.error('Erro ao verificar status:', err);
      showSnackbar(err.response?.data?.error || 'Erro ao verificar status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStartMonitoring = async (instanceName: string) => {
    try {
      await axios.post(`${API_BASE_URL}/whatsapp-qr/${instanceName}/monitor`, {
        interval: 5000
      });
      
      setMonitoring(prev => {
        const newSet = new Set(prev);
        newSet.add(instanceName);
        return newSet;
      });
      showSnackbar(`Monitoramento iniciado para ${instanceName}`, 'success');
    } catch (err: any) {
      console.error('Erro ao iniciar monitoramento:', err);
      showSnackbar(err.response?.data?.error || 'Erro ao iniciar monitoramento', 'error');
    }
  };

  const handleStopMonitoring = async (instanceName: string) => {
    try {
      await axios.delete(`${API_BASE_URL}/whatsapp-qr/${instanceName}/monitor`);
      
      setMonitoring(prev => {
        const newSet = new Set(prev);
        newSet.delete(instanceName);
        return newSet;
      });
      showSnackbar(`Monitoramento parado para ${instanceName}`, 'info');
    } catch (err: any) {
      console.error('Erro ao parar monitoramento:', err);
      showSnackbar(err.response?.data?.error || 'Erro ao parar monitoramento', 'error');
    }
  };

  const handleDisconnect = async (instanceName: string) => {
    if (!window.confirm('Tem certeza que deseja desconectar esta instância?')) return;

    try {
      await axios.post(`${API_BASE_URL}/whatsapp-qr/${instanceName}/disconnect`);
      showSnackbar(`Instância ${instanceName} desconectada`, 'success');
      fetchInstances();
    } catch (err: any) {
      console.error('Erro ao desconectar:', err);
      showSnackbar(err.response?.data?.error || 'Erro ao desconectar instância', 'error');
    }
  };

  const handleRefreshQR = async () => {
    if (!selectedInstance) return;
    
    try {
      setQrLoading(true);
      
      if (selectedInstance.provider === 'z-api' && selectedInstance.instance_id) {
        const response = await axios.get(`${API_BASE_URL}/z-api-instances/${selectedInstance.id}/qr-code`);
        if (response.data.success) {
          let qrCodeBase64 = response.data.data.qr_code || '';
          
          console.log('QR Code recebido no refresh (primeiros 200 chars):', qrCodeBase64.substring(0, 200));
          
          // Se o QR code vier como JSON string, tentar parsear
          if (qrCodeBase64 && qrCodeBase64.startsWith('{')) {
            try {
              const parsed = JSON.parse(qrCodeBase64);
              if (parsed.value) {
                qrCodeBase64 = parsed.value;
              } else if (parsed.base64) {
                qrCodeBase64 = parsed.base64;
              } else if (parsed.qrCode) {
                qrCodeBase64 = parsed.qrCode;
              }
            } catch (e) {
              console.warn('Erro ao parsear QR code JSON:', e);
            }
          }
          
          // Limpar o base64 (remover espaços, quebras de linha, etc)
          if (qrCodeBase64) {
            qrCodeBase64 = qrCodeBase64.trim().replace(/\s/g, '');
            
            // Se o QR code não começar com data:, adicionar o prefixo base64
            if (!qrCodeBase64.startsWith('data:')) {
              // Verificar se já tem o prefixo base64 ou se precisa adicionar
              if (!qrCodeBase64.startsWith('data:image')) {
                qrCodeBase64 = `data:image/png;base64,${qrCodeBase64}`;
              }
            }
            
            console.log('QR Code formatado no refresh (primeiros 100 chars):', qrCodeBase64.substring(0, 100));
          }
          
          setQrData({
            instance_name: selectedInstance.instance_id || '',
            qr_code: qrCodeBase64,
            status: response.data.data.status || 'disconnected',
            phone_number: response.data.data.phone_number,
            last_update: new Date().toISOString()
          });
          showSnackbar('QR Code atualizado!', 'success');
        }
      } else if (selectedInstance.provider === 'evolution' && selectedInstance.instance_name) {
        const response = await axios.get(`${API_BASE_URL}/whatsapp-qr/${selectedInstance.instance_name}`);
        if (response.data.success) {
          setQrData(response.data.data);
          showSnackbar('QR Code atualizado!', 'success');
        }
      }
    } catch (err: any) {
      console.error('Erro ao atualizar QR Code:', err);
      showSnackbar('Erro ao atualizar QR Code', 'error');
    } finally {
      setQrLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Carregando instâncias WhatsApp...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: 'background.default', 
      minHeight: '100vh', 
      p: 3,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(59, 130, 246, 0.06) 0%, transparent 50%)
          `,
          opacity: 0.6,
          pointerEvents: 'none'
        }}
      />

      {/* Header Sofisticado */}
      <Box className="glass-blue-card" sx={{
        mb: 3,
        p: 3,
        className: "glass-blue-card",
      }}>
        <Typography variant="h4" component="h1" sx={{
          fontWeight: 700,
          mb: 1,
          color: '#000000',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <QrCodeIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
          Gerenciar QR Codes WhatsApp
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Conecte suas instâncias WhatsApp escaneando o QR Code. Selecione o provedor abaixo.
        </Typography>
        
        {/* Seletor de Provider */}
        <FormControl fullWidth sx={{ maxWidth: 400 }}>
          <InputLabel sx={{ color: '#475569' }}>Filtrar por Provedor</InputLabel>
          <Select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value as 'all' | 'evolution' | 'z-api')}
            label="Filtrar por Provedor"
            sx={{
              color: '#000000',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '& .MuiSvgIcon-root': {
                color: '#475569',
              },
            }}
          >
            <MenuItem value="all">Todos os Provedores</MenuItem>
            <MenuItem value="evolution">Evolution API</MenuItem>
            <MenuItem value="z-api">Z-API</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {instances.map((instance) => (
          <Grid item xs={12} md={6} lg={4} key={instance.id}>
            <Card className="glass-blue-card" sx={{
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-3px) scale(1.02)',
              },
            }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: '#000000' }}>
                    {instance.name}
                  </Typography>
                  <Chip
                    icon={getStatusIcon(instance.current_status || 'disconnected')}
                    label={instance.current_status || 'Desconectado'}
                    color={getStatusColor(instance.current_status || 'disconnected') as any}
                    size="small"
                    sx={{
                      background: 'rgba(59, 130, 246, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#000000',
                      fontWeight: 600,
                    }}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ color: '#475569' }}>
                  <strong>Instância:</strong> {instance.instance_name || instance.instance_id}
                </Typography>
                {instance.provider && (
                  <Chip
                    label={instance.provider === 'evolution' ? 'Evolution API' : 'Z-API'}
                    size="small"
                    sx={{
                      mt: 0.5,
                      background: instance.provider === 'evolution' 
                        ? 'rgba(59, 130, 246, 0.2)' 
                        : 'rgba(99, 102, 241, 0.2)',
                      color: '#000000',
                      border: `1px solid ${instance.provider === 'evolution' ? '#3b82f6' : '#6366f1'}`,
                    }}
                  />
                )}

                {instance.current_phone && (
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ color: '#475569' }}>
                    <strong>Telefone:</strong> {instance.current_phone}
                  </Typography>
                )}

                {instance.last_activity && (
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ color: '#475569' }}>
                    <strong>Última atividade:</strong> {new Date(instance.last_activity).toLocaleString()}
                  </Typography>
                )}

                <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                  <Button
                    variant="contained"
                    startIcon={<QrCodeIcon />}
                    onClick={() => handleGetQRCode(instance)}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                        boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                      }
                    }}
                  >
                    QR Code
                  </Button>

                  {instance.provider === 'z-api' && (
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={() => handleCheckStatus(instance)}
                      disabled={loading}
                      size="small"
                      sx={{
                        background: 'rgba(59, 130, 246, 0.08)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        color: '#000000',
                        fontWeight: 600,
                        '&:hover': {
                          background: 'rgba(99, 102, 241, 0.2)',
                          border: '1px solid rgba(99, 102, 241, 0.5)',
                          color: '#a5b4fc',
                        },
                        '&:disabled': {
                          opacity: 0.5,
                        }
                      }}
                    >
                      Verificar Status
                    </Button>
                  )}

                  {(() => {
                    const instanceId = getInstanceIdentifier(instance);
                    return monitoring.has(instanceId) ? (
                      <Button
                        variant="outlined"
                        startIcon={<StopIcon />}
                        onClick={() => handleStopMonitoring(instanceId)}
                        size="small"
                        sx={{
                          background: 'rgba(59, 130, 246, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#000000',
                          fontWeight: 600,
                          '&:hover': {
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            color: '#ef4444',
                          }
                        }}
                      >
                        Parar
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<PlayIcon />}
                        onClick={() => handleStartMonitoring(instanceId)}
                        size="small"
                        sx={{
                          background: 'rgba(59, 130, 246, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#000000',
                          fontWeight: 600,
                          '&:hover': {
                            background: 'rgba(59, 130, 246, 0.1)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: '#3b82f6',
                          }
                        }}
                      >
                        Monitorar
                      </Button>
                    );
                  })()}

                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDisconnect(getInstanceIdentifier(instance))}
                    size="small"
                  >
                    Desconectar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog do QR Code */}
      <Dialog 
        open={qrDialogOpen} 
        onClose={() => setQrDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              QR Code - {selectedInstance?.name}
            </Typography>
            <IconButton onClick={handleRefreshQR} disabled={qrLoading}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {qrLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Carregando QR Code...
              </Typography>
            </Box>
          ) : qrData ? (
            <Box textAlign="center">
              {qrData.status === 'connected' ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="h6">
                    ✅ WhatsApp Conectado!
                  </Typography>
                  {qrData.phone_number && (
                    <Typography variant="body2">
                      Telefone: {qrData.phone_number}
                    </Typography>
                  )}
                </Alert>
              ) : qrData.qr_code ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body1">
                      Escaneie o QR Code abaixo com seu WhatsApp:
                    </Typography>
                  </Alert>
                  
                  <Box 
                    sx={{ 
                      p: 2, 
                      border: `2px dashed ${theme.palette.primary.main}`,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }}
                  >
                    <img 
                      src={qrData.qr_code}
                      alt="QR Code WhatsApp"
                      style={{ 
                        maxWidth: '100%', 
                        height: 'auto',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Status: {qrData.status === 'connecting' ? 'Conectando...' : 'Aguardando escaneamento'}
                  </Typography>
                </Box>
              ) : (
                <Alert severity="warning">
                  <Typography variant="body1">
                    QR Code não disponível. Verifique se a instância está configurada corretamente.
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="error">
              <Typography variant="body1">
                Erro ao carregar QR Code
              </Typography>
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>
            Fechar
          </Button>
          {qrData && qrData.status !== 'connected' && selectedInstance && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => handleStartMonitoring(getInstanceIdentifier(selectedInstance))}
              disabled={monitoring.has(getInstanceIdentifier(selectedInstance))}
            >
              {monitoring.has(getInstanceIdentifier(selectedInstance)) ? 'Monitorando...' : 'Iniciar Monitoramento'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar?.open || false}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar?.severity} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppQR;
